import React from 'react';
import AttendanceStudentRow from './AttendanceStudentRow';
import ReportStatusAlert from './ReportStatusAlert';

export default function AttendanceStudentList({
  students, // Keep for backwards compatibility and pagination
  historicalStudents = [], // Separate array for historical students
  regularStudents = [], // Separate array for regular students  
  noteSection = null, // Object containing note info and students
  attendance, 
  setAttendance, 
  notes, 
  setNotes,
  search, 
  setSearch, 
  clearAll, 
  currentPage, 
  totalPages, 
  setCurrentPage, 
  totalStudents, 
  studentsPerPage = 10, 
  markAllPresent,
  markAllAbsent,
  markAllLate,
  markAllExcused,
  reportStatus = 'no-report',
  databaseAutoFillInProgress = false,
  autoFillCheckComplete = true,
  databaseAutoFillEnabled = true,
  anyAutoFillEnabled = true,
  onAddStudent,
  onDeleteReport,
  // Props for enable/disable functionality
  enableUnselectedStudents = false,
  setEnableUnselectedStudents,
  // New prop to control main list rendering
  mainListReady = true
}) {
  return (
    <div className="bg-white rounded-xl p-2 divide-y divide-slate-100 w-full">
      <div className="flex items-center justify-between pb-2 px-2 gap-4">
        {/* Left side: Search bar and status */}
        <div className="flex items-center gap-4 flex-1" style={{ maxWidth: 500 }}>
          <div className="relative flex-1" style={{ maxWidth: 300 }}>
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
              </svg>
            </span>
            <input
              type="text"
              className="border border-slate-200 rounded-lg pl-4 pr-10 py-2 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
              placeholder="Search students..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          
          {/* Report Status Label */}
          <ReportStatusAlert 
            reportStatus={reportStatus}
            databaseAutoFillInProgress={databaseAutoFillInProgress}
            autoFillCheckComplete={autoFillCheckComplete}
            databaseAutoFillEnabled={databaseAutoFillEnabled}
            anyAutoFillEnabled={anyAutoFillEnabled}
          />
        </div>
        
        {/* Right side: Action buttons */}
        <div className="flex items-center gap-2">
          {/* Add Student Button */}
          <button
            className="group relative flex items-center justify-center text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg p-2 transition-all duration-200 hover:shadow-sm hover:border-blue-300"
            onClick={onAddStudent}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
            </svg>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
              Add Student
            </div>
          </button>

          <div className="h-6 w-px bg-gray-200"></div>

          {/* Delete Report Button */}
          {reportStatus === 'saved' && (
            <>
              <button
                className="group relative flex items-center justify-center text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg p-2 transition-all duration-200 hover:shadow-sm hover:border-red-300"
                onClick={onDeleteReport}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                  Delete Report
                </div>
              </button>
              <div className="h-6 w-px bg-gray-200"></div>
            </>
          )}

          {/* Clear All Button */}
          <button
            className="group relative flex items-center justify-center text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg p-2 transition-all duration-200 hover:shadow-sm hover:border-gray-300"
            onClick={clearAll}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
              Clear All
            </div>
          </button>

          <div className="h-6 w-px bg-gray-200"></div>

          {/* Mark All Present Button */}
          <button
            className="group relative flex items-center justify-center text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg p-2 transition-all duration-200 hover:shadow-sm hover:border-green-300"
            onClick={markAllPresent}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
              Mark All Present
            </div>
          </button>

          {/* Mark All Absent Button */}
          <button
            className="group relative flex items-center justify-center text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg p-2 transition-all duration-200 hover:shadow-sm hover:border-red-300"
            onClick={markAllAbsent}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
              Mark All Absent
            </div>
          </button>

          {/* Mark All Late Button */}
          <button
            className="group relative flex items-center justify-center text-sm font-medium text-yellow-700 bg-yellow-50 hover:bg-yellow-100 border border-yellow-200 rounded-lg p-2 transition-all duration-200 hover:shadow-sm hover:border-yellow-300"
            onClick={markAllLate}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
              Mark All Late
            </div>
          </button>

          {/* Mark All Excused Button */}
          <button
            className="group relative flex items-center justify-center text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg p-2 transition-all duration-200 hover:shadow-sm hover:border-blue-300"
            onClick={markAllExcused}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
              Mark All Excused
            </div>
          </button>
        </div>
      </div>
      
      {/* Combined content area for all student-related elements - each feature maintains its own scope */}
      <div className="students-content-area">
        {/* Historical Students Section */}
        {historicalStudents.length > 0 && (
          <>
            <div className="mx-2 mt-4 mb-2">
              <div className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-amber-700 font-medium">
                      Previously Enrolled Students ({historicalStudents.length})
                    </p>
                    <p className="text-xs text-amber-600 mt-1">
                      These students are no longer enrolled but appear because they have attendance records for this date.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {historicalStudents.map(student => (
              <AttendanceStudentRow
                key={student.id}
                student={student}
                status={attendance[student.id]}
                setStatus={status => setAttendance(a => ({ ...a, [student.id]: status }))}
                note={notes?.[student.id] || ''}
                setNote={note => setNotes?.(n => ({ ...n, [student.id]: note }))}
              />
            ))}
          </>
        )}

        {/* Regular Students Section */}
        {mainListReady && regularStudents.map(student => (
          <AttendanceStudentRow
            key={student.id}
            student={student}
            status={attendance[student.id]}
            setStatus={status => setAttendance(a => ({ ...a, [student.id]: status }))}
            note={notes?.[student.id] || ''}
            setNote={note => setNotes?.(n => ({ ...n, [student.id]: note }))}
          />
        ))}

        {/* Note Section */}
        {noteSection && (
          <>
            <div className="mx-2 my-3">
              <div className="px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700">
                        <strong>Note:</strong> {noteSection.studentsWithoutStatusCount} student{noteSection.studentsWithoutStatusCount !== 1 ? 's' : ''} below may appear unselected because they may have been enrolled into this class after the record was taken. Click enable if you want to access these students.
                      </p>
                    </div>
                  </div>
                  {setEnableUnselectedStudents && (
                    <button
                      onClick={() => setEnableUnselectedStudents(!enableUnselectedStudents)}
                      className="ml-4 px-3 py-1 text-sm font-medium text-blue-700 bg-blue-100 rounded hover:bg-blue-200 transition-colors"
                    >
                      {enableUnselectedStudents ? 'Disable' : 'Enable'}
                    </button>
                  )}
                </div>
              </div>
            </div>
            {/* Students enrolled after the report */}
            {noteSection.studentsWithoutStatus.map(student => (
              <AttendanceStudentRow
                key={student.id}
                student={student}
                status={attendance[student.id]}
                setStatus={status => setAttendance(a => ({ ...a, [student.id]: status }))}
                note={notes?.[student.id] || ''}
                setNote={note => setNotes?.(n => ({ ...n, [student.id]: note }))}
              />
            ))}
          </>
        )}
      </div>
      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 py-4 px-4">
          <div className="text-sm text-slate-700 font-medium">
            {totalStudents === 0
              ? 'Showing 0 of 0 students'
              : `Showing ${(currentPage - 1) * studentsPerPage + 1}-${(currentPage - 1) * studentsPerPage + students.length} of ${totalStudents} students`}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-1 rounded-lg text-sm font-medium ${currentPage === 1 ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
            >
              Previous
            </button>
            <div className="flex items-center gap-1">
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index + 1}
                  onClick={() => setCurrentPage(index + 1)}
                  className={`w-8 h-8 rounded-lg text-sm font-medium ${currentPage === index + 1 ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                >
                  {index + 1}
                </button>
              ))}
            </div>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-3 py-1 rounded-lg text-sm font-medium ${currentPage === totalPages ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
} 